/**
 * Landing Bridge Provider
 *
 * 在 LandingLayout 中包裹 children，确保 section JS 执行前 bridge 已挂载到 window。
 *
 * 时序保证：
 * 1. render 阶段（同步）：创建 bridge 实例，注册 config/track/upload，挂载 window.__bridge
 * 2. useEffect：注册 navigate（依赖 useRouter）和 auth（依赖 RightContext）
 * 3. DynamicLandingHTML 的 useEffect：创建 <script> 执行 section JS
 *
 * navigate/auth 在 useEffect 注册不会有时序问题，因为 section JS 中
 * 对这些方法的调用都绑定在用户交互（按钮点击）上，而非脚本初始化时同步调用。
 */
import { useRouter } from 'next/router';
import { useContext, useEffect, useRef } from 'react';

import { RightContext } from '@/components/RightProvider/context';

import { LandingBridgeImpl } from './LandingBridgeImpl';
import { registerAuthStrategy } from './strategies/auth';
import { registerConfigStrategy } from './strategies/config';
import { registerGeneratorStrategy } from './strategies/generator';
import { registerTrackStrategy } from './strategies/track';
import { registerUploadStrategy } from './strategies/upload';

export const LandingBridgeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const router = useRouter();
  const rightContext = useContext(RightContext);
  const bridgeRef = useRef<LandingBridgeImpl | null>(null);

  // render 阶段同步创建 bridge，确保在任何 useEffect 之前 window.__bridge 已可用
  if (!bridgeRef.current && typeof window !== 'undefined') {
    const bridge = new LandingBridgeImpl();
    registerConfigStrategy(bridge);
    registerTrackStrategy(bridge);
    registerUploadStrategy(bridge);
    registerGeneratorStrategy(bridge);
    (window as any).__bridge = bridge;
    bridgeRef.current = bridge;
  }

  // 依赖 React hooks 的 Strategy 在 useEffect 中注册
  useEffect(() => {
    const bridge = bridgeRef.current;
    if (!bridge) return;

    // Navigate strategy
    bridge.register('navigate', (path: string, options?: { replace?: boolean }) => {
      if (options?.replace) {
        router.replace(path);
      } else {
        router.push(path);
      }
    });

    // Auth strategy
    registerAuthStrategy(bridge, rightContext.getUserLoginState);
  }, [router, rightContext.getUserLoginState]);

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      if (typeof window !== 'undefined') {
        delete (window as any).__bridge;
      }
      bridgeRef.current = null;
    };
  }, []);

  return <>{children}</>;
};
